import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Snackbar,
  Alert,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  LinearProgress,
  Backdrop,
  Modal,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import JSZip from "jszip";
import { MultipleMediaUpload } from "services/apiService";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const LinearProgressWithLabel = ({ value }) => (
  <Box display="flex" alignItems="center" width="100%" mt={2}>
    <Box width="100%" mr={1}>
      <LinearProgress variant="determinate" value={value} />
    </Box>
    <Typography variant="body2" color="textSecondary">{`${Math.round(
      value
    )}%`}</Typography>
  </Box>
);

const MediaZipFile = () => {
  const [validImages, setValidImages] = useState([]);
  const [invalidFiles, setInvalidFiles] = useState([]);
  const [zipFileName, setZipFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [upIsloading, setIssuploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [openError, setOpenError] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const navigate = useNavigate();

  const onDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file || file?.type !== "application/zip") {
      setErrorMessage("Only ZIP files are allowed!");
      setOpenError(true);
      return;
    }

    setZipFileName(file.name);
    setValidImages([]);
    setInvalidFiles([]);
    setLoading(true);
    setProgress(0);

    const zip = new JSZip();
    const zipData = await zip.loadAsync(file);
    const validFormats = ["jpg", "png", "gif", "svg", "jpeg", "webp"];
    let extractedValidImages = [];
    let extractedInvalidFiles = [];
    const totalFiles = Object.keys(zipData.files).length;
    let processedFiles = 0;

    for (const filename of Object.keys(zipData.files)) {
      if (zipData.files[filename].dir) continue;

      const fileExt = filename.split(".").pop().toLowerCase();
      const actualFileName = filename.split("/").pop();

      if (validFormats.includes(fileExt)) {
        const fileData = await zipData.files[filename].async("blob");
        extractedValidImages.push({
          name: actualFileName?.replace(/\s+/g, "_"),
          url: URL.createObjectURL(fileData),
        });
      } else {
        extractedInvalidFiles.push(actualFileName);
      }

      processedFiles++;
      setProgress((processedFiles / totalFiles) * 100);
    }
    extractedValidImages.sort((a, b) => a.name.localeCompare(b.name));
    setValidImages(extractedValidImages);
    setInvalidFiles(extractedInvalidFiles);
    setLoading(false);
  };

  const removeImage = (index) => {
    setValidImages((prevImages) => prevImages.filter((_, i) => i !== index));
  };

  const removeFile = () => {
    setZipFileName("");
    setValidImages([]);
    setInvalidFiles([]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { "application/zip": [] },
  });

  const handleUploadFile = async () => {
    try {
      setIssuploading(true);
      setProgress(0);
      setOpenModal(true);
      const formData = new FormData();
      const fileNames = [];
      const chunkSize = 100;
      const chunks = [];
      console.log(chunks);

      for (let i = 0; i < validImages.length; i += chunkSize) {
        chunks.push(validImages.slice(i, i + chunkSize));
      }

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const chunk = chunks[chunkIndex];

        formData.delete("file_name");
        formData.delete("FileNames");

        await Promise.all(
          chunk.map(async (image) => {
            const response = await fetch(image.url);
            const blob = await response.blob();
            const file = new File([blob], image.name, { type: blob.type });

            formData.append("file_name", file);
            fileNames.push(image.name);
          })
        );

        formData.append("FileNames", JSON.stringify(fileNames));
        const response = await MultipleMediaUpload(formData);
        if (response.status_code !== 200) {
          throw new Error("Upload failed");
        }
        setProgress(((chunkIndex + 1) / chunks.length) * 100);
      }

      toast.success("Uploaded successfully");
      navigate("/media-list");
    } catch (error) {
      console.log(error);
      toast.error("Upload failed!");
    } finally {
      setIssuploading(false);
      setOpenModal(false);
    }
  };

  return (
    <Box p={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography
          sx={{
            backgroundColor: "#f9f9c5",
            color: "#333",
            padding: "10px",
            border: "1px solid #e6e600",
            fontSize: "16px",
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          Note! Only ZIP files are supported
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleUploadFile}
          disabled={upIsloading || !validImages.length}
          sx={{ borderRadius: "0px" }}
        >
          {upIsloading
            ? "Uploading..."
            : `Upload (${validImages.length} items)`}
        </Button>
      </Box>
      <Box
        {...getRootProps()}
        sx={{
          border: "2px dashed #ccc",
          p: 4,
          textAlign: "center",
          cursor: "pointer",
          backgroundColor: "#f4eeee",
          position: "relative",
        }}
      >
        <input {...getInputProps()} />
        {zipFileName ? (
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography mt={2}>{zipFileName}</Typography>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={removeFile}
              sx={{ marginTop: "10px" }}
            >
              Remove File
            </Button>
          </Box>
        ) : (
          <Typography>
            Drag & drop a ZIP file here, or click to upload
          </Typography>
        )}
      </Box>

      {loading && (
        <Box display="flex" flexDirection="column" alignItems="center" mt={2}>
          <LinearProgressWithLabel value={progress} />
          <Typography>Extracting files... Please wait ⏳</Typography>
        </Box>
      )}

      <Grid container spacing={2} mt={2}>
        {validImages.map((image, index) => (
          <Grid item xs={1.2} key={index}>
            <Card sx={{ position: "relative", borderRadius: "8px" }}>
              <IconButton
                sx={{ position: "absolute", top: 5, right: 5 }}
                onClick={() => removeImage(index)}
              >
                <FontAwesomeIcon icon={faXmark} />
              </IconButton>
              <CardMedia
                component="img"
                image={image.url}
                alt={image.name}
                sx={{
                  height: 100,
                  objectFit: "contain",
                  backgroundColor: "#f5f5f5",
                }}
              />
              <CardContent sx={{ p: 1 }}>
                <Typography mt={1} variant="body2" textAlign="center">
                  {image.name}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <br />

      {invalidFiles.length > 0 && (
        <Box mt={3}>
          <Typography variant="h6" color="error">
            ⚠️ Invalid Files:
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {invalidFiles.join(", ")}
          </Typography>
        </Box>
      )}

      <Snackbar
        open={openError}
        autoHideDuration={3000}
        onClose={() => setOpenError(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setOpenError(false)} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
      <Modal
        open={openModal}
        aria-labelledby="upload-progress"
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          style: { backgroundColor: "rgba(0,0,0,0.6)" },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            p: 4,
            borderRadius: "3px",
            boxShadow: 24,
            width: 300,
            textAlign: "center",
          }}
        >
          <Typography id="upload-progress" variant="h6" gutterBottom>
            Uploading...
          </Typography>
          <LinearProgressWithLabel value={progress} />
        </Box>
      </Modal>
    </Box>
  );
};

export default MediaZipFile;
